import { useMemo } from "react";
import type { RingClientApi } from "../../../shared/api-contract";
import type { EditorStore } from "../../../state/editor/EditorStore";
import { useManifest } from "./useManifest";
import { VIEWER_ORIGIN } from "./viewerOrigin";

/**
 * The scene-look LIBRARY — full `.vjson` snapshots (camera, post FX, ground,
 * scene materials) applied over the loaded model with `loadVjsonUrl`.
 *
 * Shared by Advanced's Looks panel and Basic's Scenes panel, so both offer the
 * same scenes, apply them through the same backdrop rule, and agree on which
 * one is applied.
 */

/** Curated one-click scene looks shipped alongside the sample models. */
export const BUNDLED_SCENE_LOOKS: { id: string; label: string }[] = [
  { id: "8", label: "8 — Hero" },
  { id: "Liori_0052", label: "Liori" },
  { id: "Ajaffe-vjson", label: "Ajaffe" },
  { id: "Ajaffe-dummy", label: "Ajaffe (dummy)" },
];

export interface SceneLook {
  id: string;
  label: string;
  url: string;
  /** Rendered preview, when the library has one ("" otherwise). */
  thumb: string;
}

/** The bundled looks, then the curated library ones (a curated preset carries
 *  an absolute fileUrl, so both apply through the same `loadVjsonUrl`). */
export function useSceneLooks(): SceneLook[] {
  const { items: curated, urlFor, thumbFor } = useManifest(null, "scene");
  return useMemo(
    () => [
      ...BUNDLED_SCENE_LOOKS.map((l) => ({ id: l.id, label: l.label, url: `${VIEWER_ORIGIN}/models/${l.id}.vjson`, thumb: "" })),
      ...curated.map((c) => ({ id: c.id, label: c.label, url: urlFor(c), thumb: thumbFor(c) })),
    ],
    [curated, urlFor, thumbFor],
  );
}

/**
 * Module scope on purpose, and shared by both panels:
 *
 * - `appliedId` — which look is applied. LeftPanel unmounts a panel body every
 *   time it closes, so component state would drop the highlight on every
 *   close/reopen. It is deliberately NOT persisted through `ui.lastApplied`:
 *   that survives a page reload while the viewer reboots to its base project,
 *   so a tile would claim a look that is no longer applied.
 * - `keepBackdropFlat` — whether a look may re-arm backdrop tone-mapping. These
 *   looks are verbatim iJewel captures, and two of them ("8 — Hero", "Liori")
 *   set `tonemapBackground`/`tonemapGround`, which ACES-rolls a linear 1.0
 *   backdrop down to ~#e7e7e7 — so the canvas AND every snapshot go grey no
 *   matter which background colour is picked. That is the one thing a look
 *   must not do behind the user's back. A look repainting the backdrop COLOUR
 *   is a different matter and stays allowed: it is visible the instant the tile
 *   is clicked, and the Background panel undoes it.
 */
export const sceneLookState: { appliedId: string | null; keepBackdropFlat: boolean } = {
  appliedId: null,
  keepBackdropFlat: true,
};

/**
 * Reconcile the look that just landed with the backdrop rule above. Returns
 * what to tell the user, or null when there is nothing to say. Run it BEFORE
 * `resyncAfterLook`, so the tone-mapping checkboxes re-seed from the final
 * state rather than the intermediate one.
 */
export async function reconcileBackdrop(ring: RingClientApi, keepFlat: boolean, what: string): Promise<string | null> {
  const fx = await ring.getPostFX();
  if (!fx.tonemapBackground && !fx.tonemapGround) return null;
  if (!keepFlat) {
    return `${what} tone-maps the backdrop — pictures will come out filmic grey whatever background colour is set.`;
  }
  await ring.setPostFX({ tonemapBackground: false, tonemapGround: false });
  return `${what} wanted a tone-mapped backdrop; left it flat so your background colour renders as picked.`;
}

/**
 * A vjson replays camera, post FX, ground and every scene material, so the
 * slices the inspector renders from are stale the moment it lands.
 */
export function resyncAfterLook(store: EditorStore) {
  return Promise.all([store.refresh("scene"), store.refresh("postFX"), store.refresh("material")]);
}
